import {
    Database,
    Info,
    FileText,
    Phone,
    Store,
    MapPin,
    Users,
    Target,
    Calendar,
    CheckCircle2,
    AlertTriangle
} from 'lucide-react';
import { categorizeOfferContent, formatOfferText, parseTextStructure } from '../../../utils/textUtils';

export default function ExpectedDataTab({ expectedData, loading }) {
    if (loading) {
        return (
            <div className="p-8 space-y-8">
                <div className="bg-gradient-to-r from-slate-50 to-slate-100/80 rounded-2xl p-6 border border-slate-200/60">
                    <h3 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                        <div className="p-2 bg-white rounded-xl shadow-sm border border-slate-200/60">
                            <Database size={24} className="text-slate-700" />
                        </div>
                        Expected Database Data
                    </h3>
                    <p className="text-slate-600 mt-2 font-medium">Reference data for validation comparison</p>
                </div>
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-200 border-t-orange-600"></div>
                    <span className="ml-4 text-slate-600 font-medium text-lg">Loading expected data...</span>
                </div>
            </div>
        );
    }

    if (!expectedData || typeof expectedData !== 'object') {
        return (
            <div className="p-8 space-y-8">
                <div className="bg-gradient-to-r from-slate-50 to-slate-100/80 rounded-2xl p-6 border border-slate-200/60">
                    <h3 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                        <div className="p-2 bg-white rounded-xl shadow-sm border border-slate-200/60">
                            <Database size={24} className="text-slate-700" />
                        </div>
                        Expected Database Data
                    </h3>
                    <p className="text-slate-600 mt-2 font-medium">Reference data for validation comparison</p>
                </div>
                <div className="text-center py-8 text-slate-500">
                    No expected data available
                </div>
            </div>
        );
    }

    return (
        <div className="p-8 space-y-8">
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-50 to-slate-100/80 rounded-2xl p-6 border border-slate-200/60">
                <h3 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                    <div className="p-2 bg-white rounded-xl shadow-sm border border-slate-200/60">
                        <Database size={24} className="text-slate-700" />
                    </div>
                    Expected Database Data
                </h3>
                <p className="text-slate-600 mt-2 font-medium">Reference data for validation comparison</p>
            </div>

            <div className="space-y-8">
                {/* Database Query Debug */}
                <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-blue-100 rounded-xl border border-blue-300">
                            <Info size={20} className="text-blue-600" />
                        </div>
                        <h4 className="font-bold text-xl text-blue-900">Database Query Debug</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white/80 rounded-xl p-4 border border-blue-200/60">
                            <div className="text-sm space-y-3">
                                <div className="flex justify-between">
                                    <span className="font-bold text-blue-800">Company Name:</span>
                                    <span className="font-mono bg-blue-100 px-2 py-1 rounded text-blue-900">"{expectedData?.company?.name || 'N/A'}"</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-bold text-blue-800">Internal ID:</span>
                                    <span className="font-mono bg-blue-100 px-2 py-1 rounded text-blue-900">{expectedData?.company?.internalId || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-bold text-blue-800">Address Data:</span>
                                    <span className={`px-2 py-1 rounded font-bold ${expectedData?.address ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                                        {expectedData?.address ? 'Yes' : 'No'}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white/80 rounded-xl p-4 border border-blue-200/60">
                            <div className="text-sm space-y-3">
                                <div className="flex justify-between">
                                    <span className="font-bold text-blue-800">Offers Found:</span>
                                    <span className="bg-blue-100 text-blue-900 px-2 py-1 rounded font-bold">{expectedData?.proofreading?.offers?.length || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-bold text-blue-800">Tracking:</span>
                                    <span className={`px-2 py-1 rounded font-bold ${expectedData?.proofreading?.tracking ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                                        {expectedData?.proofreading?.tracking ? 'Yes' : 'No'}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="font-bold text-blue-800">Counties:</span>
                                    <span className="bg-blue-100 text-blue-900 px-2 py-1 rounded font-bold">{expectedData?.proofreading?.counties?.counties?.length || 0}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    {expectedData?.address?.address && (
                        <div className="mt-4 bg-white/80 rounded-xl p-4 border border-blue-200/60">
                            <div className="flex items-center gap-2 mb-2">
                                <MapPin size={16} className="text-blue-600" />
                                <span className="font-bold text-blue-800">Address String:</span>
                            </div>
                            <div className="font-mono text-sm bg-blue-100 px-3 py-2 rounded-lg text-blue-900 break-all">
                                {expectedData.address.address}
                            </div>
                        </div>
                    )}
                </div>

                {/* No Data Message */}
                {(!expectedData.proofreading?.offers || expectedData.proofreading.offers?.length === 0) &&
                    (!expectedData.proofreading?.tracking) &&
                    (!expectedData.proofreading?.counties || !expectedData.proofreading.counties?.counties || expectedData.proofreading.counties.counties?.length === 0) &&
                    (!expectedData.proofreading?.contact) && (
                        <div className="bg-amber-50 p-6 rounded-lg border border-amber-200">
                            <h4 className="font-semibold text-amber-900 mb-2">⚠️ No Expected Data Found</h4>
                            <div className="text-sm text-amber-700 space-y-2">
                                <p>No data was found in the database for this store. This could be due to:</p>
                                <ul className="list-disc pl-5 space-y-1">
                                    <li>Company name mismatch between the store record and database entries</li>
                                    <li>Data not yet entered in the proofreading database</li>
                                    <li>Different naming conventions used in different database tables</li>
                                </ul>
                                <p className="mt-3">
                                    <strong>Search Name:</strong> "{expectedData.company?.name}"<br />
                                    Check if this exact name exists in the offers, tracking, counties, and contact tables.
                                </p>
                            </div>
                        </div>
                    )}

                {/* Expected Offers */}
                {expectedData.proofreading?.offers && expectedData.proofreading.offers.filter(offer =>
                    !(typeof offer.title === 'string' && offer.title.toLowerCase().includes('coupon')) &&
                    !(typeof offer.details === 'string' && offer.details.toLowerCase().includes('coupon')) &&
                    !(typeof offer.terms === 'string' && offer.terms.toLowerCase().includes('coupon')) &&
                    !(typeof offer.finePrint === 'string' && offer.finePrint.toLowerCase().includes('coupon')) &&
                    !(typeof offer.title === 'string' && offer.title.toLowerCase().includes('(for corp site use only)')) &&
                    !(typeof offer.details === 'string' && offer.details.toLowerCase().includes('(for corp site use only)'))
                ).length > 0 && (
                        <div className="bg-white rounded-2xl p-8 border border-slate-200/60 shadow-sm">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
                                    <FileText size={24} className="text-blue-600" />
                                </div>
                                <div>
                                    <h4 className="text-2xl font-bold text-slate-900">Expected Offers</h4>
                                    <p className="text-slate-600 font-medium">
                                        {expectedData.proofreading.offers.filter(offer =>
                                            !(typeof offer.title === 'string' && offer.title.toLowerCase().includes('coupon')) &&
                                            !(typeof offer.details === 'string' && offer.details.toLowerCase().includes('coupon')) &&
                                            !(typeof offer.terms === 'string' && offer.terms.toLowerCase().includes('coupon')) &&
                                            !(typeof offer.finePrint === 'string' && offer.finePrint.toLowerCase().includes('coupon')) &&
                                            !(typeof offer.title === 'string' && offer.title.toLowerCase().includes('(for corp site use only)')) &&
                                            !(typeof offer.details === 'string' && offer.details.toLowerCase().includes('(for corp site use only)'))
                                        ).length} offer{expectedData.proofreading.offers.filter(offer =>
                                            !(typeof offer.title === 'string' && offer.title.toLowerCase().includes('coupon')) &&
                                            !(typeof offer.details === 'string' && offer.details.toLowerCase().includes('coupon')) &&
                                            !(typeof offer.terms === 'string' && offer.terms.toLowerCase().includes('coupon')) &&
                                            !(typeof offer.finePrint === 'string' && offer.finePrint.toLowerCase().includes('coupon')) &&
                                            !(typeof offer.title === 'string' && offer.title.toLowerCase().includes('(for corp site use only)')) &&
                                            !(typeof offer.details === 'string' && offer.details.toLowerCase().includes('(for corp site use only)'))
                                        ).length !== 1 ? 's' : ''} in database
                                    </p>
                                </div>
                            </div>
                            <div className="space-y-4">
                                {expectedData.proofreading.offers.filter(offer =>
                                    !(typeof offer.title === 'string' && offer.title.toLowerCase().includes('coupon')) &&
                                    !(typeof offer.details === 'string' && offer.details.toLowerCase().includes('coupon')) &&
                                    !(typeof offer.terms === 'string' && offer.terms.toLowerCase().includes('coupon')) &&
                                    !(typeof offer.finePrint === 'string' && offer.finePrint.toLowerCase().includes('coupon')) &&
                                    !(typeof offer.title === 'string' && offer.title.toLowerCase().includes('(for corp site use only)')) &&
                                    !(typeof offer.details === 'string' && offer.details.toLowerCase().includes('(for corp site use only)'))
                                ).map((offer, i) => {
                                    const { mainContent, finePrintContent } = categorizeOfferContent(offer);

                                    return (
                                        <div key={i} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                                            {/* Main Offer Content */}
                                            <div className="p-4">
                                                {mainContent.map((content, idx) => (
                                                    <div key={idx} className="mb-3 last:mb-0">
                                                        {content.type === 'title' && (
                                                            <h5 className="font-semibold text-lg text-slate-900 mb-2">
                                                                {formatOfferText(content.content)}
                                                            </h5>
                                                        )}
                                                        {content.type === 'details' && (
                                                            <div className="text-sm text-slate-700 leading-relaxed">
                                                                <div className="flex items-start gap-2">
                                                                    <Info size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
                                                                    <div className="flex-1">
                                                                        <span className="font-medium text-slate-900">Details: </span>
                                                                        <div className="mt-1 space-y-2">
                                                                            {content.structure ? content.structure.map((item, structIdx) => (
                                                                                <div key={structIdx}>
                                                                                    {item.type === 'paragraph' && (
                                                                                        <p className="leading-relaxed">{formatOfferText(item.content)}</p>
                                                                                    )}
                                                                                    {item.type === 'bullet' && (
                                                                                        <div className="flex items-start gap-2 ml-2">
                                                                                            <span className="text-blue-600 mt-1">•</span>
                                                                                            <span className="flex-1">{formatOfferText(item.content.replace(/^[•·●▪▫‣⁃*-]\s*/, ''))}</span>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            )) : (
                                                                                <p>{formatOfferText(content.content)}</p>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {content.type === 'terms' && (
                                                            <div className="text-sm text-slate-700 leading-relaxed">
                                                                <div className="flex items-start gap-2">
                                                                    <CheckCircle2 size={14} className="text-green-600 mt-0.5 flex-shrink-0" />
                                                                    <div className="flex-1">
                                                                        <span className="font-medium text-slate-900">Terms: </span>
                                                                        <div className="mt-1 space-y-2">
                                                                            {content.structure ? content.structure.map((item, structIdx) => (
                                                                                <div key={structIdx}>
                                                                                    {item.type === 'paragraph' && (
                                                                                        <p className="leading-relaxed">{formatOfferText(item.content)}</p>
                                                                                    )}
                                                                                    {item.type === 'bullet' && (
                                                                                        <div className="flex items-start gap-2 ml-2">
                                                                                            <span className="text-green-600 mt-1">•</span>
                                                                                            <span className="flex-1">{formatOfferText(item.content.replace(/^[•·●▪▫‣⁃*-]\s*/, ''))}</span>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            )) : (
                                                                                <p>{formatOfferText(content.content)}</p>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Fine Print Section */}
                                            {finePrintContent.length > 0 && (
                                                <div className="bg-slate-50 border-t border-slate-200 p-3">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <AlertTriangle size={14} className="text-amber-600" />
                                                        <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                                                            Fine Print & Restrictions
                                                        </span>
                                                    </div>
                                                    <div className="space-y-2">
                                                        {finePrintContent.map((content, idx) => (
                                                            <div key={idx} className="text-xs text-slate-600 leading-relaxed">
                                                                {content.type === 'finePrint' && (
                                                                    <div className="pl-2 border-l-2 border-amber-200">
                                                                        <div className="space-y-1">
                                                                            {content.structure ? content.structure.map((item, structIdx) => (
                                                                                <div key={structIdx}>
                                                                                    {item.type === 'paragraph' && (
                                                                                        <p className="leading-relaxed">{item.content}</p>
                                                                                    )}
                                                                                    {item.type === 'bullet' && (
                                                                                        <div className="flex items-start gap-2 ml-1">
                                                                                            <span className="text-amber-600 mt-0.5">•</span>
                                                                                            <span className="flex-1">{item.content.replace(/^[•·●▪▫‣⁃*-]\s*/, '')}</span>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            )) : (
                                                                                <p>{content.content}</p>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                {/* Expected Tracking Numbers */}
                {expectedData.proofreading?.tracking && (
                    <div className="bg-white rounded-2xl p-8 border border-slate-200/60 shadow-sm">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-3 bg-purple-50 rounded-xl border border-purple-200">
                                <Phone size={24} className="text-purple-600" />
                            </div>
                            <div>
                                <h4 className="text-2xl font-bold text-slate-900">Expected Tracking Numbers</h4>
                                <p className="text-slate-600 font-medium">Phone DIDs for different marketing channels</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-2xl p-6 border border-purple-200/60">
                                <h5 className="font-bold text-lg text-purple-900 mb-4 flex items-center gap-2">
                                    <Phone size={18} className="text-purple-600" />
                                    Phone Numbers
                                </h5>
                                <div className="space-y-3">
                                    <div className="bg-white/80 rounded-xl p-3 border border-purple-200/60">
                                        <div className="flex justify-between items-center">
                                            <span className="font-medium text-slate-700">Main Number:</span>
                                            <span className="font-mono font-bold text-purple-900">{expectedData.proofreading.tracking.mainNumber}</span>
                                        </div>
                                    </div>
                                    <div className="bg-white/80 rounded-xl p-3 border border-purple-200/60">
                                        <div className="flex justify-between items-center">
                                            <span className="font-medium text-slate-700">PPC Branded:</span>
                                            <span className="font-mono font-bold text-purple-900">{expectedData.proofreading.tracking.ppcBrandedDID}</span>
                                        </div>
                                    </div>
                                    <div className="bg-white/80 rounded-xl p-3 border border-purple-200/60">
                                        <div className="flex justify-between items-center">
                                            <span className="font-medium text-slate-700">PPC Non-Branded:</span>
                                            <span className="font-mono font-bold text-purple-900">{expectedData.proofreading.tracking.ppcNonBrandedDID}</span>
                                        </div>
                                    </div>
                                    <div className="bg-white/80 rounded-xl p-3 border border-purple-200/60">
                                        <div className="flex justify-between items-center">
                                            <span className="font-medium text-slate-700">Facebook Windows:</span>
                                            <span className="font-mono font-bold text-purple-900">{expectedData.proofreading.tracking.facebookWindowsDID}</span>
                                        </div>
                                    </div>
                                    <div className="bg-white/80 rounded-xl p-3 border border-purple-200/60">
                                        <div className="flex justify-between items-center">
                                            <span className="font-medium text-slate-700">Facebook Siding/Doors:</span>
                                            <span className="font-mono font-bold text-purple-900">{expectedData.proofreading.tracking.facebookSidingDoorsDID}</span>
                                        </div>
                                    </div>
                                    <div className="bg-white/80 rounded-xl p-3 border border-purple-200/60">
                                        <div className="flex justify-between items-center">
                                            <span className="font-medium text-slate-700">Google Local Services:</span>
                                            <span className="font-mono font-bold text-purple-900">{expectedData.proofreading.tracking.googleLocalServicesDID}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-2xl p-6 border border-slate-200/60">
                                <h5 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
                                    <Target size={18} className="text-slate-600" />
                                    DID Mapping Logic
                                </h5>
                                <div className="space-y-3">
                                    <div className="bg-white/80 rounded-xl p-3 border border-slate-200/60">
                                        <div className="flex items-center gap-3">
                                            <span className="text-sm font-medium text-slate-600">PPC-Branded →</span>
                                            <span className="font-mono text-sm font-bold text-slate-900">{expectedData.proofreading.tracking.ppcBrandedDID}</span>
                                        </div>
                                    </div>
                                    <div className="bg-white/80 rounded-xl p-3 border border-slate-200/60">
                                        <div className="flex items-center gap-3">
                                            <span className="text-sm font-medium text-slate-600">PPC-Non-Branded →</span>
                                            <span className="font-mono text-sm font-bold text-slate-900">{expectedData.proofreading.tracking.ppcNonBrandedDID}</span>
                                        </div>
                                    </div>
                                    <div className="bg-white/80 rounded-xl p-3 border border-slate-200/60">
                                        <div className="flex items-center gap-3">
                                            <span className="text-sm font-medium text-slate-600">Facebook-Windows →</span>
                                            <span className="font-mono text-sm font-bold text-slate-900">{expectedData.proofreading.tracking.facebookWindowsDID}</span>
                                        </div>
                                    </div>
                                    <div className="bg-white/80 rounded-xl p-3 border border-slate-200/60">
                                        <div className="flex items-center gap-3">
                                            <span className="text-sm font-medium text-slate-600">Facebook-Siding →</span>
                                            <span className="font-mono text-sm font-bold text-slate-900">{expectedData.proofreading.tracking.facebookSidingDoorsDID}</span>
                                        </div>
                                    </div>
                                    <div className="bg-white/80 rounded-xl p-3 border border-slate-200/60">
                                        <div className="flex items-center gap-3">
                                            <span className="text-sm font-medium text-slate-600">More-Digital-QR →</span>
                                            <span className="font-mono text-sm font-bold text-slate-900">{expectedData.proofreading.tracking.mainNumber}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Expected Store Address */}
                {expectedData.address && (
                    <div className="bg-white rounded-2xl p-8 border border-slate-200/60 shadow-sm">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-3 bg-orange-50 rounded-xl border border-orange-200">
                                <Store size={24} className="text-orange-600" />
                            </div>
                            <div>
                                <h4 className="text-2xl font-bold text-slate-900">Expected Store Address</h4>
                                <p className="text-slate-600 font-medium">Physical location information</p>
                            </div>
                        </div>

                        {expectedData.address.address ? (
                            <div className="bg-gradient-to-br from-orange-50 to-orange-100/50 rounded-2xl p-6 border border-orange-200/60 mb-6">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="p-2 bg-orange-100 rounded-xl">
                                        <MapPin size={18} className="text-orange-600" />
                                    </div>
                                    <span className="font-bold text-lg text-orange-900">Store Showroom Address</span>
                                </div>
                                <div className="bg-white/80 rounded-xl p-4 border border-orange-200/60">
                                    <div className="text-lg font-bold text-slate-900">
                                        {expectedData.address.address}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
                                <div className="flex items-center gap-3">
                                    <AlertTriangle size={20} className="text-amber-600" />
                                    <div className="text-amber-800 font-bold">
                                        Address data found but no formatted address available
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                            {expectedData.address.street && (
                                <div className="bg-slate-50/80 rounded-xl p-4 border border-slate-200/60">
                                    <div className="text-sm font-bold text-slate-700 mb-1">Street</div>
                                    <div className="text-slate-900 font-medium">{expectedData.address.street}</div>
                                </div>
                            )}
                            {expectedData.address.city && (
                                <div className="bg-slate-50/80 rounded-xl p-4 border border-slate-200/60">
                                    <div className="text-sm font-bold text-slate-700 mb-1">City</div>
                                    <div className="text-slate-900 font-medium">{expectedData.address.city}</div>
                                </div>
                            )}
                            {expectedData.address.state && (
                                <div className="bg-slate-50/80 rounded-xl p-4 border border-slate-200/60">
                                    <div className="text-sm font-bold text-slate-700 mb-1">State</div>
                                    <div className="text-slate-900 font-medium">{expectedData.address.state}</div>
                                </div>
                            )}
                            {expectedData.address.zip && (
                                <div className="bg-slate-50/80 rounded-xl p-4 border border-slate-200/60">
                                    <div className="text-sm font-bold text-slate-700 mb-1">ZIP</div>
                                    <div className="text-slate-900 font-medium">{expectedData.address.zip}</div>
                                </div>
                            )}
                        </div>

                        <div className="bg-slate-50/80 rounded-xl p-4 border border-slate-200/60">
                            <div className="flex items-center gap-3 mb-2">
                                <Users size={16} className="text-slate-600" />
                                <span className="font-bold text-slate-700">Company Information</span>
                            </div>
                            <div className="text-slate-900 font-medium">
                                {expectedData.address.companyName || expectedData.company?.name || 'N/A'}
                            </div>
                            {expectedData.address.creativeAddress && (
                                <div className="mt-3 pt-3 border-t border-slate-200">
                                    <div className="text-sm font-bold text-slate-700 mb-1">Creative Address:</div>
                                    <div className="text-slate-600 font-medium">{expectedData.address.creativeAddress}</div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Expected Counties */}
                {expectedData.proofreading?.counties && (
                    <div className="bg-slate-50 rounded-lg p-6">
                        <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                            <MapPin size={20} className="text-green-600" />
                            Expected County Coverage
                        </h4>
                        <div className="bg-white p-4 rounded-lg border border-slate-200">
                            <div className="text-sm">
                                <div className="font-medium text-slate-900 mb-2">Creative Counties (for advertising):</div>
                                <div className="text-slate-700">
                                    {expectedData.proofreading.counties.proofreadingFormat || expectedData.proofreading.counties.formattedDisplay || 'No counties found'}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Expected Contact Info */}
                {expectedData.proofreading?.contact && (
                    <div className="bg-slate-50 rounded-lg p-6">
                        <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                            <Store size={20} className="text-orange-600" />
                            Expected Contact Information
                        </h4>
                        <div className="bg-white p-4 rounded-lg border border-slate-200">
                            <div className="text-sm space-y-2">
                                {expectedData.proofreading.contact.name && (
                                    <div><strong>Name:</strong> {expectedData.proofreading.contact.name}</div>
                                )}
                                {expectedData.proofreading.contact.phone && (
                                    <div><strong>Phone:</strong> {expectedData.proofreading.contact.phone}</div>
                                )}
                                {expectedData.proofreading.contact.email && (
                                    <div><strong>Email:</strong> {expectedData.proofreading.contact.email}</div>
                                )}
                                {expectedData.proofreading.contact.address && (
                                    <div><strong>Address:</strong> {expectedData.proofreading.contact.address}</div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Global Revisions */}
                {expectedData.proofreading?.revisions && expectedData.proofreading.revisions.length > 0 && (
                    <div className="bg-slate-50 rounded-lg p-6">
                        <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                            <Calendar size={20} className="text-indigo-600" />
                            Global Revisions ({expectedData.proofreading.revisions.length})
                        </h4>
                        <div className="space-y-3">
                            {expectedData.proofreading.revisions.map((revision, i) => (
                                <div key={i} className="bg-white p-4 rounded-lg border border-slate-200">
                                    <div className="text-sm">
                                        <div className="font-medium text-slate-900">{revision.revision}</div>
                                        <div className="text-slate-600 mt-1">Date: {revision.date}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
